import {
  Box, Flex, Text, Badge, Button, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, SimpleGrid, HStack, Tag, TagLabel, VStack,
  Radio, RadioGroup, Stack,
} from "@chakra-ui/react";
import { useState } from "react";

import { CREDIT_PACKS, PLANS } from "./constants";
import { formatAmount } from "./helpers";
import { Plan, CreditPack, SubscriptionState } from "./types";
import { Card, ProgressBar } from "./UI";

interface PlanCardProps {
    sub: SubscriptionState;
    onChangePlan: (plan: Plan) => void;
    onStartNow: () => void;
    onCancel: () => void;
}

export function PlanCard({ sub, onChangePlan, onStartNow, onCancel }: PlanCardProps) {
    const changePlanModal = useDisclosure();
    const cancelModal     = useDisclosure();
    const startNowModal   = useDisclosure();
    const creditsModal    = useDisclosure();

    const [selectedPlanId, setSelectedPlanId] = useState<string>(sub.plan.id);
    const [selectedPackId, setSelectedPackId] = useState<string>(CREDIT_PACKS[0].id);

    const currentPlan = sub.plan;
    const sigMax      = currentPlan.signaturesPerYear;
    const sigPct      = sigMax ? (sub.signaturesUsed / sigMax) * 100 : 0;

    function handleChangePlan(): void {
        const plan = PLANS.find((p) => p.id === selectedPlanId);
        if (!plan) return;
        onChangePlan(plan);
        changePlanModal.onClose();
    }

    function handleCancel(): void {
        onCancel();
        cancelModal.onClose();
    }

    function handleStartNow(): void {
        onStartNow();
        startNowModal.onClose();
    }

    function openChangePlan(): void {
        setSelectedPlanId(sub.plan.id);
        changePlanModal.onOpen();
    }

    return (
        <>
            <Card gridColumn={{ lg: "span 2" }}>
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
                    <Box>
                        <HStack spacing={3} mb={1} flexWrap="wrap">
                            <Text fontFamily="heading" fontSize="xl" color="gray.900">
                                Plan {currentPlan.name}
                            </Text>
                            {sub.isTrial && (
                                <Tag size="sm" borderRadius="full" bg="orange.50" border="1px solid" borderColor="orange.200" px={3} py={1}>
                                    <TagLabel fontSize="xs" color="orange.600" fontWeight="600">
                                        Essai — {sub.trialDaysLeft}j restants
                                    </TagLabel>
                                </Tag>
                            )}
                        </HStack>
                        <Text fontSize="sm" color="gray.400">
                            {formatAmount(currentPlan.pricePerYear)} / an · 1 mois offert ·{" "}
                            {currentPlan.signaturesPerYear
                                ? `${currentPlan.signaturesPerYear} signatures / an`
                                : "Signatures illimitées"}
                        </Text>
                    </Box>

                    <HStack spacing={2} flexWrap="wrap">
                        {sub.isTrial && (
                            <Button size="sm" bg="brand.500" color="white" _hover={{ bg: "brand.600" }} onClick={startNowModal.onOpen}>
                                Commencer maintenant
                            </Button>
                        )}
                        <Button size="sm" variant="outline" borderColor="brand.500" color="brand.500" _hover={{ bg: "brand.50" }} onClick={openChangePlan}>
                            Changer de plan
                        </Button>
                        <Button size="sm" variant="ghost" color="red.400" _hover={{ bg: "red.50" }} onClick={cancelModal.onOpen}>
                            Résilier
                        </Button>
                    </HStack>
                </Flex>

                <Divider my={5} borderColor="gray.100" />

                {sigMax ? (
                    <Box mb={currentPlan.hasCreditPacks ? 5 : 0}>
                        <Flex justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">Signatures cette année</Text>
                            <Text fontSize="sm" color={sigPct > 80 ? "red.500" : "gray.500"}>
                                <Text as="span" fontWeight="700" color="gray.800">{sub.signaturesUsed}</Text>
                                {" / "}{sigMax}
                            </Text>
                        </Flex>
                        <ProgressBar value={sub.signaturesUsed} max={sigMax} color={currentPlan.color} />
                        {sigPct > 80 && (
                            <Text fontSize="xs" color="red.400" mt={1}>Vous approchez de votre quota annuel.</Text>
                        )}
                    </Box>
                ) : (
                    <Flex align="center" gap={2} mb={0}>
                        <Box w="8px" h="8px" borderRadius="full" bg="green.400" flexShrink={0} />
                        <Text fontSize="sm" color="gray.600">
                            <Text as="span" fontWeight="600" color="gray.800">{sub.signaturesUsed}</Text>
                            {" "}signatures effectuées cette année · quota illimité
                        </Text>
                    </Flex>
                )}

                {currentPlan.hasCreditPacks && (
                    <Box bg="brand.50" borderRadius="12px" p={4} border="1px dashed" borderColor="brand.100">
                        <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                            <Box>
                                <Text fontSize="sm" fontWeight="600" color="brand.700">Besoin de signatures supplémentaires ?</Text>
                                <Text fontSize="xs" color="brand.500" mt={0.5}>Achetez un pack de crédits à la demande</Text>
                            </Box>
                            <Button size="sm" bg="brand.500" color="white" _hover={{ bg: "brand.600" }} flexShrink={0} onClick={creditsModal.onOpen}>
                                Acheter un pack
                            </Button>
                        </Flex>
                    </Box>
                )}
            </Card>

            <Modal isOpen={changePlanModal.isOpen} onClose={changePlanModal.onClose} size="lg" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Changer de plan</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                            {PLANS.map((plan) => {
                                const active = selectedPlanId === plan.id;
                                return (
                                    <Box key={plan.id} borderRadius="14px" border="2px solid"
                                         borderColor={active ? plan.color : "gray.100"} p={5} cursor="pointer"
                                         bg={active ? plan.accent + "44" : "white"} transition="all 0.2s"
                                         onClick={() => setSelectedPlanId(plan.id)}>
                                        <Text fontWeight="700" fontSize="sm" color={plan.color} mb={1}>{plan.name}</Text>
                                        <Text fontSize="2xl" fontWeight="800" color="gray.900">
                                            {formatAmount(plan.pricePerYear)}
                                            <Text as="span" fontSize="xs" fontWeight="400" color="gray.400"> / an</Text>
                                        </Text>
                                        <Divider my={3} />
                                        <VStack spacing={1} align="start">
                                            <Text fontSize="xs" color="gray.500">
                                                {plan.signaturesPerYear ? `${plan.signaturesPerYear} signatures / an` : "Signatures illimitées"}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">{plan.usersIncluded} utilisateur inclus</Text>
                                            <Text fontSize="xs" color="gray.500">
                                                +{formatAmount(plan.pricePerExtraUser)} / utilisateur supplémentaire
                                            </Text>
                                            <Text fontSize="xs" color="green.600" fontWeight="500">{plan.trialMonths} mois offert</Text>
                                            {plan.hasCreditPacks && (
                                                <Badge mt={1} colorScheme="blue" fontSize="9px" borderRadius="full">
                                                    Packs de crédits disponibles
                                                </Badge>
                                            )}
                                        </VStack>
                                    </Box>
                                );
                            })}
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={changePlanModal.onClose}>Annuler</Button>
                        <Button bg="brand.500" color="white" _hover={{ bg: "brand.600" }}
                                isDisabled={selectedPlanId === sub.plan.id} onClick={handleChangePlan}>
                            Confirmer le changement
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={cancelModal.isOpen} onClose={cancelModal.onClose} size="md" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading" color="red.500">Résilier l'abonnement</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            Êtes-vous sûr de vouloir résilier votre abonnement <strong>{sub.plan.name}</strong> ?
                            Vous conservez l'accès jusqu'à la fin de votre période en cours.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={cancelModal.onClose}>Conserver</Button>
                        <Button colorScheme="red" onClick={handleCancel}>Résilier définitivement</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={startNowModal.isOpen} onClose={startNowModal.onClose} size="md" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Commencer maintenant</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            En démarrant votre abonnement <strong>{sub.plan.name}</strong> dès maintenant,
                            votre période d'essai prendra fin et vous serez facturé{" "}
                            <strong>{formatAmount(currentPlan.pricePerYear)} / an</strong> à partir d'aujourd'hui.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={startNowModal.onClose}>Annuler</Button>
                        <Button bg="brand.500" color="white" _hover={{ bg: "brand.600" }} onClick={handleStartNow}>
                            Démarrer l'abonnement
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={creditsModal.isOpen} onClose={creditsModal.onClose} size="sm" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Acheter un pack de crédits</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <RadioGroup onChange={setSelectedPackId} value={selectedPackId}>
                            <Stack spacing={3}>
                                {CREDIT_PACKS.map((pack: CreditPack) => (
                                    <Box key={pack.id} borderRadius="12px" border="2px solid"
                                         borderColor={selectedPackId === pack.id ? "brand.500" : "gray.100"}
                                         bg={selectedPackId === pack.id ? "brand.50" : "white"}
                                         p={4} cursor="pointer" onClick={() => setSelectedPackId(pack.id)} transition="all 0.2s">
                                        <Flex justify="space-between" align="center">
                                            <Radio value={pack.id} colorScheme="blue">
                                                <Text fontSize="sm" fontWeight="500">{pack.label}</Text>
                                            </Radio>
                                            <Text fontWeight="700" color="brand.600">{formatAmount(pack.price)}</Text>
                                        </Flex>
                                    </Box>
                                ))}
                            </Stack>
                        </RadioGroup>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={creditsModal.onClose}>Annuler</Button>
                        <Button bg="brand.500" color="white" _hover={{ bg: "brand.600" }} onClick={creditsModal.onClose}>
                            Acheter
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}